
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, AlertCircle, CheckCircle, Clock, RotateCw } from "lucide-react";

// Collection status types
type Status = 'requested' | 'processing' | 'completed' | 'failed';

interface CollectionCardProps {
  title: string;
  status: Status;
  videoCount: number;
  tiktokUrl: string;
  createdAt: Date;
  zipUrl?: string;
  excelUrl?: string;
  thumbnailUrl?: string;
  onReScan?: () => void;
  onViewOriginal?: () => void;
}

const CollectionCard = ({
  title,
  status,
  videoCount,
  tiktokUrl,
  createdAt,
  zipUrl,
  excelUrl,
  thumbnailUrl,
  onReScan,
  onViewOriginal,
}: CollectionCardProps) => {
  const formattedDate = createdAt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const getStatusBadge = (status: Status) => {
    switch (status) {
      case 'requested':
        return <Badge variant="outline" className="bg-yellow-900/30 text-yellow-400 border-yellow-700"><Clock className="mr-1 h-3 w-3" /> Pending</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700"><Clock className="mr-1 h-3 w-3" /> Processing</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-900/30 text-green-400 border-green-700"><CheckCircle className="mr-1 h-3 w-3" /> Complete</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-900/30 text-red-400 border-red-700"><AlertCircle className="mr-1 h-3 w-3" /> Failed</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  return (
    <div className="bg-tokscript-card rounded-xl p-5 shadow-lg border border-gray-800">
      {thumbnailUrl && (
        <div className="mb-4 overflow-hidden rounded-lg aspect-video bg-gray-900 flex items-center justify-center">
          <img 
            src={thumbnailUrl} 
            alt={title} 
            className="w-full h-full object-cover" 
            onError={(e) => {
              e.currentTarget.src = 'https://placehold.co/600x400/0F111A/00E5FF?text=TikTok+Collection';
            }}
          />
        </div>
      )}
      
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-semibold text-lg text-white">{title || "Untitled Collection"}</h3>
        {getStatusBadge(status)}
      </div>
      
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex justify-between text-gray-400">
          <span>Videos:</span>
          <span className="text-white font-medium">{videoCount}</span>
        </div>
        
        <div className="flex justify-between text-gray-400">
          <span>Added on:</span>
          <span className="text-white">{formattedDate}</span>
        </div>

        <div className="flex flex-col gap-3 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="bg-transparent border-gray-700 hover:bg-gray-800"
            title="Open original TikTok URL"
            onClick={onViewOriginal}
          >
            <ExternalLink className="mr-2 h-4 w-4" /> 
            View Original
          </Button>
          
          <Button
            variant="outline"
            size="sm" 
            className="bg-transparent border-gray-700 hover:bg-gray-800"
            onClick={onReScan}
          >
            <RotateCw className="mr-2 h-4 w-4" />
            Re-scan Collection
          </Button>
          
          {status === 'completed' && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 bg-transparent border-gray-700 hover:bg-gray-800"
                disabled={!zipUrl}
              >
                <Download className="mr-2 h-4 w-4" />
                ZIP
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 bg-transparent border-gray-700 hover:bg-gray-800"
                disabled={!excelUrl}
              >
                <Download className="mr-2 h-4 w-4" />
                Excel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionCard;
